import React, { useEffect, useRef, useState, useMemo } from "react";
import { Switch, Route, Redirect, useHistory, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar/Sidebar.js";
import AdminNavbar from "components/Navbars/AdminNavbar.js";
import AdminFooter from "components/Footers/AdminFooter.js";
import {
  superUserRoutes,
  authRoute,
  restaurantVendorRoutes,
  retailerWholesalerRoutes,
  healthcareVendorRoutes,
  threePLPartnerRoutes,
  brandVendorRoutes,
  eventOrganizerRoutes,
} from "../routes.js";
import image3 from "assets/img/full-screen-image-3.jpg";
import RoutesContext from "../context/routesContext.js";
import { EntitlementProvider } from "../context/EntitlementContext.js";
import { CountryProvider, useCountry } from "../context/CountryContext.js";
import { PermissionsProvider, usePermissions } from "../utilities/usePermissions.js";
import { VendorAccessProvider, useVendorAccess } from "../hooks/useVendorAccess.js";
import { BranchScopeProvider, useBranchScope } from "../context/BranchScopeContext.js";
import { vendorOwnerIdOf } from "../utilities/vendorScope.js";
import HeadOfficeOnly, { headOfficeOnlyRoutes } from "../components/branch/HeadOfficeOnly.js";
import { normalizeRequiredPermission } from "../routes.js";
import { getUserFromLocalStorage } from "../utilities/user.js";
import InHouseOrderModal from "../components/InHouseOrderModal.js";
// Pinned across the top while a super user is signed in as a vendor.
import ImpersonationBanner from "../components/ImpersonationBanner.js";
import NotificationsCenter from "../views/Notifications/NotificationsCenter.js";
import { apiGetAllVendorsForSuperAdmin } from "../api/index";

// One route-tree walker for every "hide what this login may not use" rule.
// `keep(route)` answers for a single entry; a collapse group whose views all
// fall away is dropped with them. 3PL staff (requiredPermission levels) and
// vendor team members (vendorPermission keys) both filter through here, so
// there is exactly one place that knows how a route tree is pruned.
export const filterRoutes = (routes, keep) =>
  (routes || [])
    .map((route) => {
      if (!keep(route)) return null;
      if (route.collapse && route.views) {
        const views = filterRoutes(route.views, keep);
        if (views.length === 0) return null;
        return { ...route, views };
      }
      return route;
    })
    .filter(Boolean);

// Friendly fallback shown when a user navigates directly to a route they lack
// permission for (the menu item is already hidden by the Sidebar). Mirrors the
// EntitlementGate empty-state pattern rather than 404-ing.
const PermissionDenied = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
      textAlign: "center",
      padding: 24,
      color: "#475569",
    }}
  >
    <i
      className="nc-icon nc-lock-circle-open"
      style={{ fontSize: 48, color: "#ef6461", marginBottom: 16 }}
    />
    <h4 style={{ margin: 0, fontWeight: 700, color: "#0f172a" }}>
      Access restricted
    </h4>
    <p style={{ marginTop: 8, maxWidth: 420, fontSize: 14 }}>
      You don&apos;t have permission to view this page. If you believe this is a
      mistake, contact a super user to review your access.
    </p>
  </div>
);

// Wraps a route component, gating render on the RBAC permission resolved from
// the route's `requiredPermission`. SUPERUSER bypasses and we fail OPEN while
// permissions load (see usePermissions), so authorised users never see a
// flash of "restricted". Routes without `requiredPermission` render normally.
const PermissionGuard = ({ requiredPermission, children }) => {
  const { can } = usePermissions();
  const key = normalizeRequiredPermission(requiredPermission);
  if (key && !can(key)) {
    return <PermissionDenied />;
  }
  return children;
};

// Routed views, keyed by the active country so a super-admin country switch
// force-remounts every view. Switching country only changes the `x-country`
// header; remounting is the cheap, reliable way to make each view refetch
// under the new country (avoids stale country-scoped data lingering). Must
// live inside <CountryProvider> so it can read useCountry(). Non-supers have
// activeCountry === homeCountry always, so this is inert for them.
//
// The active branch (context/BranchScopeContext.js) keys the views the same
// way (4 Oct 2026): picking a branch in the navbar remounts the screen, which
// refetches its orders, sales and kitchen for that branch. It is "all" for
// every vendor without branches and for staff, so nothing changes for them.
//
// `headOfficeRoutes` are the head office pages a member limited to some
// branches cannot use. They stay out of the sidebar, but a direct link to one
// shows a plain "Head office only" page instead of bouncing to the dashboard
// without a word.
const RoutedContent = ({ getRoutes, userRoutes, landingPath, headOfficeRoutes = [] }) => {
  const { activeCountry } = useCountry();
  const { active: activeBranch } = useBranchScope();
  return (
    <Switch key={`${activeCountry}:${activeBranch}`}>
      {/* Every signed-in operator manages their own browser alerts. Vendor
          route trees do not include the staff Notifications menu group. */}
      <Route exact path="/admin/notifications" component={NotificationsCenter} />
      {getRoutes(userRoutes)}
      {headOfficeRoutes.map((route) => (
        <Route
          key={`head-office:${route.path}`}
          path={route.layout + route.path}
          render={() => <HeadOfficeOnly homePath={landingPath || "/admin/dashboard"} />}
        />
      ))}
      {userRoutes.length > 0 && (
        <Route path="/admin">
          <Redirect to={landingPath || "/admin/dashboard"} />
        </Route>
      )}
    </Switch>
  );
};

// The routed part of the layout. It sits inside VendorAccessProvider so a
// SELLER login's route tree can be pruned by the vendor team permissions
// (routes.js `vendorPermission`). The provider is async: while it loads,
// can() answers true and the entry renders; once loaded without the key the
// entry disappears from the sidebar and from <Switch>, so a direct URL falls
// through to the landing redirect. Owners and platform staff keep everything.
//
// `wholeBusinessOnly` (4 Oct 2026) drops a screen for a member limited to
// some branches: the wallet, payouts and money reports, the team, and the
// vendor's own settings are head office's, whatever the member's role says.
export const keepVendorRoute = (route, { can, isBranchScoped }) =>
  (!route.vendorPermission || can(route.vendorPermission)) &&
  !(route.wholeBusinessOnly && isBranchScoped);

const AdminShell = ({ userRoutes, gateVendorRoutes, getRoutes, landingPath, sidebarImage, sidebarBackground, children }) => {
  const location = useLocation();
  useEffect(() => {
    document.documentElement.classList.remove("nav-open");
  }, [location.pathname, location.search]);
  const { can, isBranchScoped } = useVendorAccess();
  const visibleRoutes = useMemo(
    () =>
      gateVendorRoutes
        ? filterRoutes(userRoutes, (route) => keepVendorRoute(route, { can, isBranchScoped }))
        : userRoutes,
    [userRoutes, gateVendorRoutes, can, isBranchScoped]
  );
  const headOfficeRoutes = useMemo(
    () => (gateVendorRoutes && isBranchScoped ? headOfficeOnlyRoutes(userRoutes) : []),
    [userRoutes, gateVendorRoutes, isBranchScoped]
  );

  return (
    <RoutesContext.Provider value={visibleRoutes}>
      <div className="wrapper menu-on-left">
        <Sidebar
          routes={visibleRoutes}
          image={sidebarImage}
          background={sidebarBackground}
        />
        <div className="main-panel">
          <ImpersonationBanner />
          <AdminNavbar />
          <div className="content">
            <RoutedContent
              getRoutes={getRoutes}
              userRoutes={visibleRoutes}
              landingPath={landingPath}
              headOfficeRoutes={headOfficeRoutes}
            />
          </div>
          <AdminFooter />
          <button
            type="button"
            aria-label="Close navigation"
            className="close-layer"
            onClick={() => document.documentElement.classList.remove("nav-open")}
          />
        </div>
      </div>
      {children}
    </RoutesContext.Provider>
  );
};

const Admin = () => {
  const history = useHistory();
  const user = getUserFromLocalStorage();
  const [userRoutes, setUserRoutes] = useState([]);

  // ── Global In-House Order Modal (works on every page) ────────────────────
  const userRole      = user?.result?.user_role;
  const userType      = user?.result?.userType;
  const operationMode = user?.result?.operationMode;
  const isLusukuStaff = userType === "LUSUKU STAFF";
  // operationMode is VENDOR routing. A staff account must never be steered by
  // it: a converted account can carry a stale mode (e.g. EVENT_ORGANIZER),
  // and the staff route sets do not register /admin/ticketing/dashboard, so
  // the redirect lands on an empty <Switch> (blank content area, forever).
  //
  // The same trap catches 3PL. Neither a 3PL partner nor their staff has
  // /admin/dashboard in their route set (theirs is /admin/3pl-partner-dashboard),
  // so the fallback below used to drop them on a blank content area. It went
  // unnoticed only because nothing was being hidden from them yet; the moment
  // a route is gated by permission, every staff member who reaches for it
  // lands on nothing.
  const landingPath =
    !isLusukuStaff && operationMode === "EVENT_ORGANIZER"
      ? "/admin/ticketing/dashboard"
      : !isLusukuStaff &&
        (operationMode === "3PL_PARTNER" || operationMode === "3PL_STAFF")
        ? "/admin/3pl-partner-dashboard"
        : "/admin/dashboard";
  const isSuperAdmin  = isLusukuStaff && userRole === "SUPERUSER";
  const isSupervisor  = isLusukuStaff && userRole === "SUPERVISOR";

  const [showGlobalInHouseModal, setShowGlobalInHouseModal] = useState(false);
  const [globalVendorsList, setGlobalVendorsList]           = useState([]);
  const [globalVendorsLoaded, setGlobalVendorsLoaded]       = useState(false);

  useEffect(() => {
    const openModal = async () => {
      if ((isSuperAdmin || isSupervisor) && !globalVendorsLoaded) {
        try {
          const { data } = await apiGetAllVendorsForSuperAdmin();
          setGlobalVendorsList(data?.vendors || []);
          setGlobalVendorsLoaded(true);
        } catch (_) {}
      }
      setShowGlobalInHouseModal(true);
    };
    window.addEventListener("openInHouseOrderModal", openModal);
    return () => window.removeEventListener("openInHouseOrderModal", openModal);
  }, [isSuperAdmin, isSupervisor, globalVendorsLoaded]);

  const getRoutes = useMemo(() => {
    // `inheritedPermission` lets a collapse group's requiredPermission cascade
    // to its child routes, so direct navigation to a sub-view is guarded by the
    // group's permission even though only the leaf <Route> is registered. A
    // view's own requiredPermission (if any) takes precedence over the group's.
    const flattenRoutes = (routes, inheritedPermission) => {
      return routes.flatMap((prop) => {
        const effectivePermission = prop.requiredPermission || inheritedPermission;
        if (prop.collapse) {
          return flattenRoutes(prop.views, effectivePermission);
        }
        if (prop.layout === "/admin") {
          return (
            <Route
              path={prop.layout + prop.path}
              render={(props) => (
                <PermissionGuard requiredPermission={effectivePermission}>
                  <prop.component {...props} />
                </PermissionGuard>
              )}
              key={prop.path}
            />
          );
        }
        return null;
      }).filter(Boolean);
    };

    return (routes) => flattenRoutes(routes, null);
  }, []);

  useEffect(() => {
    // Helper function to check if user has required permission level
    const hasPermission = (permissions, requiredKey, requiredLevel) => {
      if (!requiredKey) return true; // No permission required
      const levels = { 'none': 0, 'view': 1, 'full': 2 };
      const userLevel = levels[permissions?.[requiredKey] || 'none'];
      const needed = levels[requiredLevel] || 0;
      return userLevel >= needed;
    };

    // Filter routes based on user permissions (for 3PL staff)
    const filterRoutesByPermissions = (routes, userPermissions, isPartner) => {
      if (isPartner) return routes; // Partners see all routes

      // `ownerOnly` is for screens that act on the PARTNER ACCOUNT itself
      // rather than on the partner's operation: account security, for one. No
      // permission level can express that, because the question is not how
      // much access a staff member has but whose account the screen edits.
      // This function only ever runs for staff, so the flag simply removes it.
      return filterRoutes(routes, (route) => {
        if (route?.ownerOnly === true) return false;
        if (route.requiredPermission) {
          const { key, level } = route.requiredPermission;
          return hasPermission(userPermissions, key, level);
        }
        return true;
      });
    };

    const updateRoutes = (user) => {
      if (user === null) return authRoute;
      if (user?.result?.userType === "LUSUKU STAFF") {
        // ONE permission-driven tree for ALL staff roles. Visibility is decided
        // entirely by the effective permission set (role defaults, then per-user
        // GRANT/DENY overrides from the feature editor): the Sidebar hides any
        // entry whose requiredPermission the user lacks, and PermissionGuard
        // blocks direct URLs. Splitting the tree by role meant a granted
        // feature (e.g. Brands or Ticketing for a supervisor) had no menu to
        // reveal, so the feature editor silently did nothing. Staff must also
        // never fall through to the vendor branches below (a stale
        // operationMode on a converted account would hand them a vendor
        // sidebar). The backend stays the hard enforcement boundary.
        return superUserRoutes;
      }
      if (user?.result?.user_role === "SUPERVISOR") {
        if (user?.result?.operationMode === "RESTAURANT") {
          return restaurantVendorRoutes;
        }
        if (["RETAILER", "WHOLESALER"].includes(user?.result?.operationMode)) {
          return retailerWholesalerRoutes;
        }
        if (user?.result?.operationMode === "HEALTHCARE") {
          return healthcareVendorRoutes;
        }
      }
      // Allow vendors without SUPERVISOR role to access their dashboards
      if (user?.result?.operationMode === "RESTAURANT") {
        return restaurantVendorRoutes;
      }
      if (["RETAILER", "WHOLESALER"].includes(user?.result?.operationMode)) {
        return retailerWholesalerRoutes;
      }
      if (user?.result?.operationMode === "BRAND_OWNER") {
        return brandVendorRoutes;
      }
      if (user?.result?.operationMode === "EVENT_ORGANIZER") {
        return eventOrganizerRoutes;
      }
      // Handle legacy SELLER userType (for retailers/wholesalers with operationMode "ONLINE")
      if (user?.result?.userType === "SELLER" || user?.result?.operationMode === "ONLINE") {
        return retailerWholesalerRoutes;
      }
      if (user?.result?.operationMode === "HEALTHCARE") {
        return healthcareVendorRoutes;
      }
      // Handle 3PL Partner login - they see all routes
      if (user?.result?.operationMode === "3PL_PARTNER") {
        return threePLPartnerRoutes;
      }
      // Handle 3PL Staff login - filter routes based on their permissions
      if (user?.result?.operationMode === "3PL_STAFF") {
        const userPermissions = user?.result?.permissions || {};
        return filterRoutesByPermissions(threePLPartnerRoutes, userPermissions, false);
      }
      return authRoute;
    };

    const newRoutes = updateRoutes(user);

    if (JSON.stringify(newRoutes) !== JSON.stringify(userRoutes)) {
      setUserRoutes(newRoutes);
    }

    const unListen = history.listen(() => {
      const newUser = getUserFromLocalStorage();
      const updatedRoutes = updateRoutes(newUser);

      if (JSON.stringify(updatedRoutes) !== JSON.stringify(userRoutes)) {
        setUserRoutes(updatedRoutes);
      }
    });

    return unListen;
  }, [user, userRoutes, history]);

  const sidebarImageRef = useRef(image3);
  const sidebarBackgroundRef = useRef("black");

  return (
    <CountryProvider>
    <EntitlementProvider>
    <PermissionsProvider>
    <VendorAccessProvider>
    <BranchScopeProvider>
      <AdminShell
        userRoutes={userRoutes}
        gateVendorRoutes={userType === "SELLER"}
        getRoutes={getRoutes}
        landingPath={landingPath}
        sidebarImage={sidebarImageRef.current}
        sidebarBackground={sidebarBackgroundRef.current}
      >
        {/* Global In-House Order Modal, rendered at layout level so it works on every page */}
        {showGlobalInHouseModal && (
          <InHouseOrderModal
            open={showGlobalInHouseModal}
            onClose={() => setShowGlobalInHouseModal(false)}
            isSuperAdmin={isSuperAdmin || isSupervisor}
            allVendors={globalVendorsList}
            // The owner's id: a team member places orders for the business.
            vendorId={vendorOwnerIdOf(user)}
            vendorType={operationMode}
            vendorName={user?.result?.businessName || user?.result?.name}
          />
        )}
      </AdminShell>
    </BranchScopeProvider>
    </VendorAccessProvider>
    </PermissionsProvider>
    </EntitlementProvider>
    </CountryProvider>
  );
};

export default Admin;
